import bodyStyles from '../article-body/ArticleBody.module.scss'
import parse from 'html-react-parser'
import { useTranslation } from 'next-i18next/pages'
import { Heading, Video, Img, Card, CallToAction } from '@boltenergy-be/design-system'
import { ContentBlocksProps } from 'components/article/content-blocks/types'
import { CONTENT_BLOCKS } from 'types/cms/enums'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import classNames from 'classnames'
import Markdown from 'react-markdown'
import remarkGfmTable from 'utils/remarkGfmTable'
import { ALLOWED_MARKDOWN_ELEMENTS } from 'components/article/content-blocks/constants'
import { getQuoteFontSize, setVimeoUrl } from 'components/article/content-blocks/utils'
import StartSimulationButton from 'components/start-simulation-button/StartSimulationButton'
import styles from './ContentBlocks.module.scss'

const ContentBlocks = ({ contentBlocks }: ContentBlocksProps) => {
  // i18n
  const { t } = useTranslation('encyclopedia')

  return contentBlocks.map((block, idx) => {
    switch (block.__component) {
      case CONTENT_BLOCKS.RICH_TEXT:
        return (
          <div className={bodyStyles['rich-text']} key={`${block.__component}-${idx}`}>
            {block.text ? <BlocksRenderer content={block.text} /> : parse(block.body ?? '')}
          </div>
        )

      case CONTENT_BLOCKS.TABLE:
        return block.markdown ? (
          <div className={bodyStyles.table} key={`${block.__component}-${idx}`}>
            <Markdown allowedElements={ALLOWED_MARKDOWN_ELEMENTS} remarkPlugins={[remarkGfmTable]}>
              {block.markdown}
            </Markdown>
          </div>
        ) : null

      case CONTENT_BLOCKS.IMAGE:
        return (
          <figure className={bodyStyles.image} key={`${block.__component}-${idx}`}>
            <Img
              publicId={block.image.provider_metadata.public_id}
              alt={block.image.alternativeText ?? ''}
              width={block.image.width}
              height={block.image.height}
            />
            {block.customCaption && <figcaption className="text-subdued px-100 pt-100">{block.customCaption}</figcaption>}
          </figure>
        )

      case CONTENT_BLOCKS.GOOD_TO_KNOW:
        return (
          <Card key={`${block.__component}-${idx}`} className={bodyStyles['good-to-know']} color="soft">
            <Heading as="h4" serif>
              {t('goodToKnow')}
            </Heading>
            {block.text ? <BlocksRenderer content={block.text} /> : parse(block.information ?? '')}
          </Card>
        )

      case CONTENT_BLOCKS.QUOTE:
        return (
          <figure key={`${block.__component}-${idx}`} className={bodyStyles['quote-wrapper']}>
            <blockquote className={bodyStyles.quote} style={{ fontSize: getQuoteFontSize(block.quote) }}>
              &quot;{block.quote}&quot;
            </blockquote>
            {block.author && <figcaption>- {block.author}</figcaption>}
          </figure>
        )

      case CONTENT_BLOCKS.VIMEO:
        return (
          <iframe
            key={`${block.__component}-${idx}`}
            className={bodyStyles.vimeo}
            title={block.title}
            src={setVimeoUrl(block)}
            allowFullScreen
            allow="autoplay"
          />
        )

      case CONTENT_BLOCKS.VIDEO:
        return (
          <div key={`${block.__component}-${idx}`} className={bodyStyles['video-wrapper']}>
            <Video
              className={classNames(bodyStyles.video, bodyStyles[block.orientation || 'landscape'])}
              publicId={block.video.provider_metadata.public_id}
            />
          </div>
        )

      case CONTENT_BLOCKS.CALL_TO_ACTION:
        return (
          <CallToAction
            key={`${block.__component}-${idx}`}
            fluid
            title={block.title || t('callToAction.title')}
            action={<StartSimulationButton text={block.action} className={styles.action} />}
            color="primary"
            classnames={{ container: styles['call-to-action'] }}
          >
            <p>{block.description || t('callToAction.description')}</p>
          </CallToAction>
        )

      default:
        return null
    }
  })
}

export default ContentBlocks
